/**
 * Pure helpers for the staff patient-identity config editor (Form + Package
 * builders).
 *
 * Kept in a plain .ts module (no React) so they are unit-testable under the
 * node-env vitest setup — the .tsx builders stay presentational.
 *
 * Semantics mirror the server (`packages/shared/identity`):
 *   - the DEFAULT config is firstName + lastName + dob, all required (what a
 *     NULL `identity_fields_json` column means);
 *   - an empty array `[]` = no identity step (owner falls back to contact /
 *     legacy preview);
 *   - any other array = an explicit, ordered config.
 * The builders send `isDefaultConfig(cfg) ? null : cfg` so a default writes the
 * canonical NULL and every other state writes deterministically.
 */
import type { IdentityFieldDef } from './api.js';

export type IdentityFieldKey = IdentityFieldDef['key'];

/** One selectable identity field with its human label, in catalog order. */
export interface IdentityFieldOption {
  key:   IdentityFieldKey;
  label: string;
}

/** The fixed catalog — order here drives the editor rows AND the order a newly
 *  included field settles into. */
export const IDENTITY_FIELD_CATALOG: readonly IdentityFieldOption[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName',  label: 'Last name' },
  { key: 'dob',       label: 'Date of birth' },
  { key: 'email',     label: 'Email' },
  { key: 'phone',     label: 'Phone' },
];

/** DEFAULT config when a form/package has no explicit identity_fields_json.
 *  firstName + lastName + dob, all required (mirrors DEFAULT_IDENTITY_FIELDS). */
export const DEFAULT_IDENTITY_CONFIG: readonly IdentityFieldDef[] = [
  { key: 'firstName', required: true },
  { key: 'lastName',  required: true },
  { key: 'dob',       required: true },
];

const CATALOG_INDEX: Record<IdentityFieldKey, number> = {
  firstName: 0, lastName: 1, dob: 2, email: 3, phone: 4,
};

/** True when `key` is included in the config. */
export function isIncluded(cfg: readonly IdentityFieldDef[], key: IdentityFieldKey): boolean {
  return cfg.some((f) => f.key === key);
}

/**
 * Add or remove `key` from the config, returning a NEW array. A newly included
 * field defaults to required and settles into catalog order so the editor rows
 * and the served identity screen read consistently regardless of click order.
 */
export function toggleField(cfg: readonly IdentityFieldDef[], key: IdentityFieldKey): IdentityFieldDef[] {
  if (isIncluded(cfg, key)) return cfg.filter((f) => f.key !== key);
  return [...cfg, { key, required: true }].sort(
    (a, b) => CATALOG_INDEX[a.key] - CATALOG_INDEX[b.key],
  );
}

/** Set the `required` flag on an included field, returning a NEW array (no-op
 *  copy when the field is not included). */
export function setRequired(
  cfg: readonly IdentityFieldDef[], key: IdentityFieldKey, required: boolean,
): IdentityFieldDef[] {
  return cfg.map((f) => (f.key === key ? { ...f, required } : f));
}

/** True when `cfg` is exactly the DEFAULT trio (same keys, order, and required
 *  flags) — the builders send NULL instead of an explicit array in that case. */
export function isDefaultConfig(cfg: readonly IdentityFieldDef[]): boolean {
  if (cfg.length !== DEFAULT_IDENTITY_CONFIG.length) return false;
  return cfg.every(
    (f, i) => f.key === DEFAULT_IDENTITY_CONFIG[i]!.key && f.required === DEFAULT_IDENTITY_CONFIG[i]!.required,
  );
}
